import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Eye, 
  EyeOff, 
  Lock, 
  User, 
  AlertTriangle,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(null);

  // Admin credentials (in real app, these would be stored securely on server)
  const ADMIN_CREDENTIALS = [
    {
      username: 'admin',
      password: 'nyaysahaya2024',
      name: 'Super Admin',
      role: 'super_admin'
    },
    {
      username: 'manager',
      password: 'manager123',
      name: 'Platform Manager',
      role: 'manager'
    },
    {
      username: 'support',
      password: 'support456',
      name: 'Support Admin',
      role: 'support'
    }
  ];

  // Check if device is mobile
  useEffect(() => {
    const checkDevice = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
      if (isMobileDevice) {
        setShowMobileWarning(true);
      }
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Check for lockout
  useEffect(() => {
    const savedLockoutTime = localStorage.getItem('adminLockoutTime');
    if (savedLockoutTime) {
      const lockoutEnd = new Date(parseInt(savedLockoutTime));
      const now = new Date();
      
      if (now < lockoutEnd) {
        setIsLocked(true);
        setLockoutTime(lockoutEnd);
      } else {
        // Lockout expired
        localStorage.removeItem('adminLockoutTime');
        localStorage.removeItem('adminLoginAttempts');
        setLoginAttempts(0);
        setIsLocked(false);
        setLockoutTime(null);
      }
    }

    const savedAttempts = localStorage.getItem('adminLoginAttempts');
    if (savedAttempts) {
      setLoginAttempts(parseInt(savedAttempts));
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLocked) {
      toast.error('Account is temporarily locked. Please try again later.');
      return;
    }

    if (!formData.username || !formData.password) {
      toast.error('Please enter both username and password');
      return;
    }

    setIsLoading(true);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Check credentials
    const admin = ADMIN_CREDENTIALS.find(
      cred => cred.username === formData.username && cred.password === formData.password
    );

    if (admin) {
      // Successful login
      toast.success(`Welcome back, ${admin.name}!`);
      
      // Store admin session
      const adminSession = {
        username: admin.username,
        name: admin.name,
        role: admin.role,
        loginTime: new Date().toISOString(),
        isAuthenticated: true
      };
      
      localStorage.setItem('adminSession', JSON.stringify(adminSession));
      
      // Reset login attempts
      setLoginAttempts(0);
      localStorage.removeItem('adminLoginAttempts');
      localStorage.removeItem('adminLockoutTime');
      
      // Navigate to admin dashboard
      navigate('/admin-dashboard');
    } else {
      // Failed login
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem('adminLoginAttempts', newAttempts.toString());

      if (newAttempts >= 5) {
        // Lock account for 15 minutes
        const lockoutEnd = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes
        setLockoutTime(lockoutEnd);
        setIsLocked(true);
        localStorage.setItem('adminLockoutTime', lockoutEnd.getTime().toString());
        
        toast.error('Too many failed attempts. Account locked for 15 minutes.');
      } else {
        const remainingAttempts = 5 - newAttempts;
        toast.error(`Invalid credentials. ${remainingAttempts} attempts remaining.`);
      }
    }

    setIsLoading(false);
  };

  const handleBackToApp = () => {
    navigate('/dashboard');
  };

  const getRemainingLockoutTime = () => {
    if (!lockoutTime) return '';
    
    const now = new Date();
    const diff = lockoutTime - now;
    
    if (diff <= 0) {
      setIsLocked(false);
      setLockoutTime(null);
      localStorage.removeItem('adminLockoutTime');
      return '';
    }
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Mobile warning component
  if (showMobileWarning) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Desktop Only
          </h2>
          <p className="text-gray-600 mb-6">
            Admin panel is only accessible on desktop devices. Please use a computer or tablet to access this page.
          </p>
          <button
            onClick={handleBackToApp}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Back to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={handleBackToApp}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to App</span>
          </button>
          
          <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Panel</h1>
          <p className="text-gray-600">Secure access for authorized team members</p>
        </div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-xl p-8 border border-gray-200"
        >
          {isLocked ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Account Locked</h2>
              <p className="text-gray-600 mb-4">
                Too many failed login attempts. Please try again in:
              </p>
              <div className="text-2xl font-bold text-red-600 mb-6">
                {getRemainingLockoutTime()}
              </div>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    placeholder="Enter admin username"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    placeholder="Enter admin password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {loginAttempts > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
                    <span className="text-sm text-yellow-800">
                      {5 - loginAttempts} login attempts remaining
                    </span>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    <span>Sign In to Admin Panel</span>
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-center space-x-2 text-blue-800">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Secure Admin Access</span>
            </div>
            <p className="text-xs text-blue-600 mt-1">
              Only authorized team members can access this panel
            </p>
          </div>
        </div>

        {/* Demo Credentials (remove in production) */}
        <div className="mt-6 text-center">
          <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <summary className="text-sm font-medium text-gray-700 cursor-pointer">
              Demo Credentials (Development Only)
            </summary>
            <div className="mt-3 space-y-2 text-left">
              {ADMIN_CREDENTIALS.map((cred, index) => (
                <div key={index} className="text-xs text-gray-600">
                  <strong>{cred.name}:</strong> {cred.username} / {cred.password}
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
